// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useMemo, useCallback, useEffect } from 'react'
import { GripVertical, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react'

/**
 * Per-user column reorder + hide. State persisted in localStorage.
 * Key format: `owl_cols_${viewId}` → JSON { order: string[], hidden: string[] }
 */

interface ColPref { order: string[]; hidden: string[] }

function loadPref(viewId: string): ColPref | null {
  try { const s = localStorage.getItem(`owl_cols_v2_${viewId}`); return s ? JSON.parse(s) : null } catch { return null }
}
function savePref(viewId: string, p: ColPref) {
  try { localStorage.setItem(`owl_cols_v2_${viewId}`, JSON.stringify(p)) } catch { /* storage full/blocked — prefs just don't persist */ }
}

// RCA-ASP-66: `labels` renders friendly names while state keys on the stable column KEY —
// label-keyed state collapsed same-label duplicates and survived admin deletions.
export function useColumnManager(viewId: string, allColumns: string[], labels?: Record<string, string>) {
  const [pref, setPref] = useState<ColPref>(() => loadPref(viewId) || { order: [], hidden: [] })
  const [showManager, setShowManager] = useState(false)

  // Merge saved pref with actual columns (handle new/removed columns)
  const visibleColumns = useMemo(() => {
    const hiddenSet = new Set(pref.hidden)
    // Start with saved order, filter to existing columns
    const known = new Set(allColumns)
    const ordered = pref.order.filter(c => known.has(c))
    // Append any new columns not in saved order
    for (const c of allColumns) {
      if (!ordered.includes(c)) ordered.push(c)
    }
    return ordered.filter(c => !hiddenSet.has(c))
  }, [allColumns, pref])

  const allOrdered = useMemo(() => {
    const known = new Set(allColumns)
    const ordered = pref.order.filter(c => known.has(c))
    for (const c of allColumns) { if (!ordered.includes(c)) ordered.push(c) }
    return ordered
  }, [allColumns, pref.order])

  const isHidden = useCallback((col: string) => pref.hidden.includes(col), [pref.hidden])

  const toggleHide = useCallback((col: string) => {
    const next = { ...pref, hidden: isHidden(col) ? pref.hidden.filter(c => c !== col) : [...pref.hidden, col] }
    setPref(next); savePref(viewId, next)
  }, [pref, viewId, isHidden])

  const moveUp = useCallback((col: string) => {
    const arr = [...allOrdered]
    const idx = arr.indexOf(col)
    if (idx <= 0) return
    ;[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]
    const next = { ...pref, order: arr }
    setPref(next); savePref(viewId, next)
  }, [allOrdered, pref, viewId])

  const moveDown = useCallback((col: string) => {
    const arr = [...allOrdered]
    const idx = arr.indexOf(col)
    if (idx < 0 || idx >= arr.length - 1) return
    ;[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]
    const next = { ...pref, order: arr }
    setPref(next); savePref(viewId, next)
  }, [allOrdered, pref, viewId])

  const reset = useCallback(() => {
    const next = { order: [], hidden: [] }
    setPref(next); savePref(viewId, next)
  }, [viewId])

  // Reload pref when viewId changes
  useEffect(() => {
    setPref(loadPref(viewId) || { order: [], hidden: [] })
  }, [viewId])

  // E-CV-201: drag-to-reorder — POINTER-based, not HTML5 DnD. Native dragstart
  // silently refuses to fire in plenty of nested/overflow layouts (the first
  // ship of this feature showed the grab cursor but never grabbed), while
  // pointer events always deliver. Hold the grip, move over another row, the
  // list reorders live; release persists. Arrows kept for keyboard access.
  const [dragging, setDragging] = useState<string | null>(null)
  const reorderTo = useCallback((src: string, target: string) => {
    if (src === target) return
    const cur = [...allOrdered]
    const from = cur.indexOf(src)
    const to = cur.indexOf(target)
    if (from < 0 || to < 0) return
    cur.splice(from, 1)
    cur.splice(to, 0, src)
    const next = { ...pref, order: cur }
    setPref(next); savePref(viewId, next)
  }, [allOrdered, pref, viewId])
  useEffect(() => {
    if (!dragging) return
    const up = () => setDragging(null)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => { window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up) }
  }, [dragging])

  const managerUI = showManager ? (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 10, marginBottom: 10, background: 'var(--color-surface)', maxHeight: 300, overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Manage columns</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={reset} style={smBtn}>Reset</button>
          <button onClick={() => setShowManager(false)} style={smBtn}>Done</button>
        </div>
      </div>
      {allOrdered.map((col, idx) => (
        <div key={col}
          onPointerEnter={() => { if (dragging && dragging !== col) reorderTo(dragging, col) }}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 0', fontSize: 12,
                   opacity: isHidden(col) ? 0.5 : 1,
                   outline: dragging === col ? '1.5px solid var(--color-accent, #0f766e)' : 'none',
                   borderRadius: 4,
                   background: dragging === col ? 'var(--color-surface-hover, rgba(15,118,110,0.08))' : 'transparent' }}>
          <span
            onPointerDown={e => { e.preventDefault(); setDragging(col) }}
            style={{ cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', display: 'inline-flex', padding: '2px 2px' }}
            title="Drag to reorder"
          >
            <GripVertical size={12} style={{ color: 'var(--color-text-mute)' }} />
          </span>
          <button onClick={() => moveUp(col)} disabled={idx === 0} style={arrowBtn}><ChevronUp size={12} /></button>
          <button onClick={() => moveDown(col)} disabled={idx === allOrdered.length - 1} style={arrowBtn}><ChevronDown size={12} /></button>
          <button onClick={() => toggleHide(col)} style={arrowBtn} title={isHidden(col) ? 'Show' : 'Hide'}>
            {isHidden(col) ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <span style={{ flex: 1 }}>{labels?.[col] ?? col}</span>
        </div>
      ))}
    </div>
  ) : null

  const toggleButton = (
    <button onClick={() => setShowManager(!showManager)} style={smBtn}>
      {showManager ? 'Hide columns' : 'Columns'}
    </button>
  )

  return { visibleColumns, managerUI, toggleButton }
}

const smBtn: React.CSSProperties = {
  fontSize: 11, padding: '3px 8px', background: 'var(--color-surface)',
  border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
}
const arrowBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
  color: 'var(--color-text-soft)', display: 'inline-flex',
}
